/**
 * The onboarding wizard that is responsible for showing, navigating, fulfilling and skipping steps
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { noop, find, findIndex } from 'lodash';
import { withStyles } from '@material-ui/core/styles';
import OnBoardingOverlay from './OnBoardingOverlay';

const styles = theme => ({
	root: {
		minHeight: '100%',
		display: 'flex',
		flexDirection: 'column'
	},
	step: {
		display: 'flex',
		flexDirection: 'column',
		flexGrow: '1',
		padding: '20px 60px 10px'
	},
	navigation: {
		display: 'flex',
		alignSelf: 'center',
		marginBottom: 33
	},
	navigationItem: {
		padding: 7,
		'&:after': {
			content: '""',
			display: 'block',
			width: 9,
			height: 9,
			borderRadius: 20,
			boxShadow: `inset 0 0 0 1px ${theme.palette.colors.alpha}`,
			transition: theme.transitions.create('box-shadow')
		}
	},
	navigationItemActive: {
		'&:after': {
			boxShadow: `inset 0 0 0 20px ${theme.palette.colors.alpha}`
		}
	},
	overlay: {
		transition: theme.transitions.create('transform', {
			easing: theme.transitions.easing.easeOut,
			duration: theme.transitions.duration.leavingScreen
		})
	}
});

@withStyles(styles)
class OnboardingWizard extends Component {

	constructor(props) {
		super(props);
		this.go = this.go.bind(this);
		this.prev = this.prev.bind(this);
		this.next = this.next.bind(this);
		this.onStepFulfilled = this.onStepFulfilled.bind(this);
		this.onStepSkipped = this.onStepSkipped.bind(this);
		this.state = {
			step: this.getStep(props),
			stay: false
		};
	}

	componentWillReceiveProps(props) {
		const step = this.getStep(props);

		if (!this.state.stay && step !== this.state.step) {
			this.setState({ step }, () => {
				this.props.onStepActive(step);
			});
		}
	}

	render() {
		const { root, step, navigation, overlay } = this.props.classes;

		if (!this.steps.length) {
			return null;
		}

		return (
			<div className={root}>
				<div className={step}>
					{this.renderStep()}
				</div>
				<div className={navigation}>
					{this.renderNavigation()}
				</div>
				<OnBoardingOverlay oId={this.state.step} classes={{ root: overlay }} />
			</div>
		);
	}

	/**
	 * Renders single step based on the step from state
	 * @returns The onboarding step
	 */
	renderStep() {
		const { step } = this.state;
		const Step = find(this.steps, Step => Step.oId === step);

		return (
			<Step
				onboarding={{
					fulfilled: this.props.fulfilledSteps,
					skipped: this.props.skippedSteps,
					complete: this.props.onCompleted,
					fulfillStep: this.onStepFulfilled,
					skipStep: this.onStepSkipped,
					nextStep: this.next,
					prevStep: this.prev
				}}
			/>
		);
	}

	/**
	 * Render the onboarding navigation
	 * @returns The array of <div /> elements
	 */
	renderNavigation() {
		const { navigationItem, navigationItemActive } = this.props.classes;
		const { step } = this.state;

		return this.steps.map(Step => {
			const { oId } = Step;

			const stepClassName = classNames({
				[navigationItem]: true,
				[navigationItemActive]: oId === step
			});

			return (
				<div
					key={`onboarding-nav-${oId}`}
					className={stepClassName}
					data-step={oId}
				/>
			);
		});
	}

	/**
	 * Goes to the given step
	 * @param step Step identifier
	 */
	go(step) {
		if (step !== this.state.step) {
			this.setState({ step }, () => {
				this.props.onStepActive(step);
			});
		}
	}

	/**
	 * Navigates to next step
	 */
	next() {
		const { step: current } = this.state;
		const step = this.findNextStep() || current;
		this.go(step);
	}

	/**
	 * Navigates to previous step
	 */
	prev() {
		const { step: current } = this.state;
		const step = this.findPrevStep() || current;
		this.go(step);
	}

	/**
	 * Finds next step based on the current step
	 * @returns Step identifier
	 */
	findNextStep() {
		const { step } = this.state;
		const index = findIndex(this.steps, Step => Step.oId === this.state.step);
		const { oId } = this.steps[index + 1] || step;
		return oId;
	}

	/**
	 * Finds prev step based on the current step
	 * @returns Step identifier
	 */
	findPrevStep() {
		const { step } = this.state;
		const index = findIndex(this.steps, Step => Step.oId === this.state.step);
		const { oId } = this.steps[index - 1] || step;
		return oId;
	}

	/**
	 * Fulfills a step
	 * @param stay Flag that indicates if the wizard should not navigate to next step
	 */
	onStepFulfilled(stay = false) {
		const { step } = this.state;
		this.setState({ stay }, () => {
			this.props.onStepFulfilled(step);
		});
	}

	/**
	 * Skips a step
	 */
	onStepSkipped() {
		const { step } = this.state;
		this.props.onStepSkipped(step);
	}

	/**
	 * Returns a step based on current props. It checks the fulfilled and skipped steps and returns
	 * only next step that doesn't exist in both arrays.
	 * @param props Current properties
	 * @returns {Object|undefined} Steps if found
	 */
	getStep(props) {
		const { fulfilledSteps, skippedSteps } = props;

		for (const Step of this.steps) {
			if (fulfilledSteps.indexOf(Step.oId) === -1 && skippedSteps.indexOf(Step.oId) === -1) {
				return Step.oId;
			}
		}
	}

	/**
	 * Steps getter
	 * @returns The onboarding steps
	 */
	get steps() {
		return this.props.steps;
	}
}

OnboardingWizard.propTypes = {
	classes: PropTypes.object,
	steps: PropTypes.array.isRequired,
	fulfilledSteps: PropTypes.array,
	skippedSteps: PropTypes.array,
	onStepFulfilled: PropTypes.func,
	onStepSkipped: PropTypes.func,
	onStepActive: PropTypes.func,
	onCompleted: PropTypes.func
};

OnboardingWizard.defaultProps = {
	fulfilledSteps: [],
	skippedSteps: [],
	onStepFulfilled: noop,
	onStepSkipped: noop,
	onStepActive: noop,
	onCompleted: noop
};

export default OnboardingWizard;


// WEBPACK FOOTER //
// ./src/components/OnBoardingWizard/OnBoardingWizard.js